<template>
  <el-dialog
            title="E-Delivery Approval"
            :visible.sync="showDialogValue"
            width="1000px"
            height="100%"
            :close-on-click-modal=false
            :close-on-press-escape=false>
    <div v-loading="loading">
      <el-form inline>
        <el-form-item label="From" class="line-height-3">
          <el-date-picker :readonly="readOnlyDates" v-model="dateFrom" format="MM/dd/yyyy" placeholder="Pick a print date"></el-date-picker>
        </el-form-item>
        <el-form-item label="To" class="line-height-3">
          <el-date-picker :readonly="readOnlyDates" v-model="dateTo" format="MM/dd/yyyy" placeholder="Pick a print date"></el-date-picker>
        </el-form-item>
        <el-button type="primary"  @click="onFilterClick()">Filter</el-button>
        <el-button v-if="dateFrom || dateTo" type="primary"  @click="onClearFilterClick">Clear filter</el-button>
        <div v-if="!adminMode">
          <el-button type="success" @click="onSaveAndProcessUpdatesClick">Save and Process</el-button>
        </div>
        <div v-if="adminMode">
          <el-button type="success" @click="onApplyChangesClick">Apply Changes</el-button>
        </div>
      </el-form>
      <el-table class="u-mt3 edelivery-table" :data="approvalsTableData"
                :row-class-name="baEdeliverablesTableRowClassName">
        <el-table-column label="Policy Number" property="policyNumber" width="150" sortable ></el-table-column>
        <el-table-column label="Label" property="label" width="150"></el-table-column>
        <el-table-column label="Title" property="title"></el-table-column>
        <el-table-column prop="printDate" class-name="approvalsTable__print-date" label="Print Date" width="120">
          <template slot-scope="scope">
            {{ scope.row.printDate | moment('MM/DD/YYYY') }}
          </template>
        </el-table-column>
        <el-table-column label="Delivery status" width="270">
          <template slot-scope="scope">
            <el-select v-model="scope.row.event_status" required style="width: 235px" @change="onGroupRowDeliveryStatusUpdate(scope.row)">
              <el-option
                      v-for="status in statusChoices"
                      :key="status"
                      :label="eDeliveryStatusToHumanString(status)"
                      :value="status">
              </el-option>
            </el-select>
            <div style="display: inline" v-if="scope.row.updated">*</div>
          </template>
        </el-table-column>

        <el-table-column type="expand" >
          <template slot-scope="recordUiScope">
            <div class="linked-edeliverables-title">
              <p><b>deliverables: </b></p>
            </div>
            <div class="linked-edeliverables-body">
              <p v-for="linked_edelivery in recordUiScope.row.linked_edeliveries">
              <a :href="extractTitleDataFromMetadata(linked_edelivery.edeliverable_metadata).url"
                 target="_blank" title="Will open in new tab">
                {{ extractTitleDataFromMetadata(linked_edelivery.edeliverable_metadata).title }}</a>
              </p>
            </div>
          </template>
        </el-table-column>
      </el-table>
      <span slot="footer" class="dialog-footer">
      <el-button @click="showDialogValue = false">Close</el-button>
    </span>
    </div>
  </el-dialog>
</template>

<script>
import { mapGetters } from 'vuex'
import {deepClone, axiosWrapper} from '@/shared/api.utils'
import config from '@/config'
import axios from 'axios'
import _ from 'lodash'
import {humanizeEdeliveryStatus, getEdeliveryRowCssStyleByStatus, convertDateToYYYYMMDD} from '@/shared/misc.utils'

export default {
  name: 'ba-approval-dialog',
  components: {},

  data () {
    return {
      showDialogValue: false,
      dateFrom: '',
      dateTo: '',
      companyId: null,
      approvalsTableData: [],
      approvalsOriginalData: [],
      readOnlyDates: false,
      adminMode: false,
      loading: false,
    }
  },
  computed: {
    ...mapGetters('shared', ['companyById']),
    ...mapGetters('settings', ['getSetting']),
    showExcludeAndDontPrintStateForNonAdmins () {
      return this.getSetting('e_delivery.show_exclude_and_dont_print_on_regular_edelivery_approval_page') === 'true'
    },
    changesSetOnApprovalPage () {
      // make dict of updated user-events using updated flag value
      let recordsToUpdate = {}
      this.approvalsTableData.forEach(groupedItemsRow => {
        if (groupedItemsRow.updated) {
          recordsToUpdate[groupedItemsRow.id] = groupedItemsRow.event_status
        }
      })
      return recordsToUpdate
    },
    statusChoices () {
      let carrierStatusChoices = ['ALLOWED FOR IMPORT', 'WAITING FOR APPROVAL', 'EXCLUDE FROM EDELIVERY']
      let adminStatusChoices = carrierStatusChoices.concat(["EXCLUDE AND DON'T PRINT"])
      return this.adminMode || this.showExcludeAndDontPrintStateForNonAdmins ? adminStatusChoices : carrierStatusChoices
    }
  },
  methods: {
    eDeliveryStatusToHumanString (status) {
      return humanizeEdeliveryStatus(status)
    },
    async askUserConfirmation () {
      try {
        await this.$confirm('Are you sure?', '', {
          confirmButtonText: 'OK',
          cancelButtonText: 'Cancel',
          type: 'warning'
        })
        return true
      } catch (error) {
        // user pressed "Cancel" button
        return false
      }
    },
    async onApplyChangesClick () {
      if (!await this.askUserConfirmation()) return

      // check if nothing to save
      if (Object.keys(this.changesSetOnApprovalPage).length === 0) return

      this.loading = true
      try {
        await this.applyUpdatesOnApprovalPage()
      } finally {
        this.loading = false
      }
    },
    async onSaveAndProcessUpdatesClick () {
      if (!await this.askUserConfirmation()) return
      this.loading = true
      try {
        if (Object.keys(this.changesSetOnApprovalPage).length !== 0) {
          await this.saveAndProcessUpdates() // apply and send
        } else {
          await this.sendApprovedEdeliveryEvents() // nothing to apply, just send
        }
      } finally {
        this.loading = false
        this.showDialogValue = false
      }
    },
    getCompany () {
      return this.companyById(this.companyId)
    },
    extractTitleDataFromMetadata (metadata) {
      let company = this.getCompany()
      let title = metadata.file ? metadata.file.title : metadata.form.title
      let edeliverableBcId = metadata.file ? metadata.file.id : metadata.form.id
      return {
        url: company.britecore_url + '/gateway?file=core.uploads&method=getAttachment&id=' + edeliverableBcId,
        title: title
      }
    },
    async showDialog (options) {
      this.companyId = options.companyId
      this.dateFrom = options.dateFrom
      this.dateTo = options.dateTo
      this.readOnlyDates = options.readOnlyDates
      this.adminMode = options.adminMode
      this.showDialogValue = true

      this.loading = true
      try {
        await this.requestEdeliveryEventsToRevisit()
      } finally {
        this.loading = false
      }
    },
    async onFilterClick () {
      this.loading = true
      try {
        await this.requestEdeliveryEventsToRevisit()
      } finally {
        this.loading = false
      }
    },
    async onClearFilterClick () {
      this.dateFrom = ''
      this.dateTo = ''
      this.loading = true
      try {
        await this.requestEdeliveryEventsToRevisit()
      } finally {
        this.loading = false
      }
    },
    onGroupRowDeliveryStatusUpdate (row) {
      let original = _.find(this.approvalsOriginalData, edeliveryUserEvent => {
        return edeliveryUserEvent.id === row.id
      })
      row.updated = original.event_status !== row.event_status
    },
    async applyUpdatesOnApprovalPage () {
      // update entities status in BA
      try {
        await axiosWrapper(axios.post(`${config.url}/company/${this.companyId}/update_edelivery_events_status/`, {
          records: this.changesSetOnApprovalPage
        }))
      } catch (error) {
        this.$message({
          type: 'error',
          message: `Failed to perform action. ${error.response.message}`
        })
        throw error
      }

      await this.requestEdeliveryEventsToRevisit(true)
    },
    async saveAndProcessUpdates () {
      // update entities status in BA
      await this.applyUpdatesOnApprovalPage()
      await this.sendApprovedEdeliveryEvents()
    },
    groupForUiRepresentation (data) {
      data.forEach(edeliveryUserEvent => {
        let docs = []
        let forms = []
        edeliveryUserEvent.linked_edeliveries.forEach(
          edelivery => {
            let metadata = edelivery.edeliverable_metadata
            if (metadata.form !== undefined) {
              forms.push(edelivery)
            } else {
              docs.push(edelivery)
            }
          }
        )

        let label, title
        if (docs.length) {
          let doc = docs[0]
          let metadata = doc.edeliverable_metadata
          let file = metadata.file
          edeliveryUserEvent.policyNumber = doc.policy_number
          label = file.label
          title = file.title
          edeliveryUserEvent.printDate = file.print_date
        } else {
          let form = forms[0]
          let metadata = form.edeliverable_metadata
          let relatedFile = metadata.related_file
          edeliveryUserEvent.policyNumber = form.policy_number
          label = relatedFile.label
          title = relatedFile.title
          edeliveryUserEvent.printDate = relatedFile.print_date
        }
        if (docs.length && !forms.length) {
          edeliveryUserEvent.title = title
          edeliveryUserEvent.label = label
        } else if (docs.length && forms.length) {
          edeliveryUserEvent.title = title + ' with ' + forms.length.toString() + ' Form(s)'
          edeliveryUserEvent.label = label + ' & Forms'
        } else if (!docs.length && forms.length) {
          edeliveryUserEvent.title = forms.length.toString() + ' Form(s) related to ' + title
          edeliveryUserEvent.label = 'Form(s)'
        }
        edeliveryUserEvent.updated = false
      })
      return data
    },
    prepareToSetWaitingForApprovalToApproved (data) {
      //  switch 'WAITING FOR APPROVAL' to 'ALLOWED FOR IMPORT' state but don't save
      data.forEach(edeliveryUserEvent => {
        if (edeliveryUserEvent.event_status === 'WAITING FOR APPROVAL') {
          edeliveryUserEvent.event_status = 'ALLOWED FOR IMPORT'
          edeliveryUserEvent.updated = true
        }
      })
    },
    async requestEdeliveryEventsToRevisit (forwardError) {
      let dictToSend = {}
      if (this.dateFrom !== '') {
        dictToSend.from_date = convertDateToYYYYMMDD(this.dateFrom)
      }
      if (this.dateTo !== '') {
        dictToSend.to_date = convertDateToYYYYMMDD(this.dateTo)
      }
      dictToSend.filter_states = this.adminMode
        ? ['ALLOWED FOR IMPORT', 'WAITING FOR APPROVAL', 'EXCLUDE FROM EDELIVERY', "EXCLUDE AND DON'T PRINT"]
        : ['ALLOWED FOR IMPORT', 'WAITING FOR APPROVAL']
      try {
        let eventsResponse = await axiosWrapper(
          axios.post(`${config.url}/company/${this.companyId}/request_edelivery_events_to_revisit/`, dictToSend))
        let data = eventsResponse.data
        data.forEach(row => { row.updated = false })
        // here we create structure for grouped records. It will be used by UI table
        this.approvalsTableData = this.groupForUiRepresentation(data)
        // this is UI initial data clone. We use it to determine which entities have status change
        this.approvalsOriginalData = deepClone(this.approvalsTableData)
        // after cloning switch it to "approved" state but don't save
        this.prepareToSetWaitingForApprovalToApproved(this.approvalsTableData)
      } catch (error) {
        this.$message({
          type: 'error',
          message: `Failed to perform action. ${error.response.message}`
        })
        if (forwardError) throw error
      }
    },
    getClassByStatus (status) {
      return getEdeliveryRowCssStyleByStatus(status)
    },
    baEdeliverablesTableRowClassName ({row, rowIndex}) {
      let status = row.event_status
      return this.getClassByStatus(status)
    },
    async sendApprovedEdeliveryEvents () {
      let dictToSend = {}
      if (this.dateFrom !== '') {
        dictToSend.from_date = convertDateToYYYYMMDD(this.dateFrom)
      }
      if (this.dateTo !== '') {
        dictToSend.to_date = convertDateToYYYYMMDD(this.dateTo)
      }
      try {
        await axiosWrapper(
          axios.post(`${config.url}/company/${this.companyId}/send_approved_edelivery_events/`, dictToSend))
      } catch (error) {
        this.$message({
          type: 'error',
          message: `Failed to perform action. ${error.response.message}`
        })
        throw error
      }
    },
  },
}
</script>

<style scoped>
  .line-height-3 {
    line-height: 3em;
  }
</style>

<style lang="scss">
  .approvalsTable {
    &__print-date {
      & > div {
        white-space: nowrap !important;
      }
    }
  }
</style>
